import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';
import { Container } from '@components/layout/Grid';
import { BundleQuery, Video } from '@gql/generated';
import Trailer from '@organisms/video-player/Trailer';
import { Avatar, AvatarFallback, AvatarImage } from 'src/ui/avatar';
import { Trans } from '@lingui/react';

const Wrapper = styled(Container)`
  background-color: #d9e7e7;
`;

type Props = {
  preview: BundleQuery['bundle']['preview'];
};

export const PreviewSection = (props: Props) => {
  const { preview } = props;
  const { video, title } = preview;

  return (
    <Wrapper fullWidth>
      <div className="w-full px-2">
        <p className="max-w-2xl text-[28px] font-bold md:text-[32px]/[1.4] text-green-950 text-center mx-auto px-2">
          {title}
        </p>
        <div className="flex flex-col w-full max-w-3xl rounded mx-auto overflow-hidden [&_.videoplayer-tag]:bg-[#33847E]">
          <Trailer
            {...{
              elementID: 'trailer',
              video: preview.video as Video,
              autoPlay: false,
              thumbnailImage: video.assets.thumbnailUrl,
              isLimited: false,
              tags: [preview.label],
            }}
          />
          <div className="flex items-center gap-4 p-4 bg-white">
            <Avatar className="size-10">
              <AvatarImage
                src={video.taxonomies.instructors[0]?.assets.squareImage}
              />
              <AvatarFallback className="bg-[url(/static/watermark.png)] bg-cover" />
            </Avatar>
            <div className="flex flex-col gap-1 text-green-950">
              <p className="text-lg font-semibold m-0">{video.content.title}</p>
              <p
                className="font-light m-0 opacity-90"
                dangerouslySetInnerHTML={{
                  __html: preview.description,
                }}
              ></p>
            </div>
          </div>
        </div>
        <a className="flex flex-col text-xs text-green-950 text-center font-semibold uppercase py-8">
          <Trans id="bundle.preview_section.scroll_for_more" />
          <div className="pt-4">
            <ScrollIcon />
          </div>
        </a>
      </div>
    </Wrapper>
  );
};

const ScrollIconAnimation = styled.div`
  .chevron-1,
  .chevron-2,
  .chevron-3 {
    width: 20px;
    height: 15px;
    margin: 0 auto;

    //
    path {
      stroke-width: 3px;
      transform: scale(2);
      transform-origin: center;
    }
  }

  .chevron-1 {
    animation: 2s cubic-bezier(0.33, -0.44, 0.52, 1) 0.1s infinite alternate
      scrollDown1;
  }
  .chevron-2 {
    animation: 2s cubic-bezier(0.33, -0.44, 0.52, 1) 0.1s infinite alternate
      scrollDown2;
  }
  .chevron-3 {
    animation: 2s cubic-bezier(0.33, -0.44, 0.52, 1) 0.1s infinite alternate
      scrollDown3;
  }

  @keyframes scrollDown1 {
    from {
      transform: translateY(0%);
    }

    to {
      transform: translateY(220%);
    }
  }

  @keyframes scrollDown2 {
    from {
      transform: translateY(0%);
    }

    to {
      transform: translateY(120%);
    }
  }

  @keyframes scrollDown3 {
    from {
      transform: translateY(0%);
    }

    to {
      transform: translateY(20%);
    }
  }
`;

const ScrollIcon = () => {
  return (
    <ScrollIconAnimation className="flex flex-col size-10 mx-auto">
      <ChevronDown className="chevron-1" />
      <ChevronDown className="chevron-2" />
      <ChevronDown className="chevron-3" />
    </ScrollIconAnimation>
  );
};
